import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { LuMail } from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Avatar,
  Badge,
  Box,
  Card,
  Heading,
  HStack,
  Icon,
  Button,
  NativeSelect,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { TableSkeleton } from "../components/TableSkeleton";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { FilterBar, type FilterNode } from "../components/FilterBar";
import { FilterCount } from "../components/FilterCount";
import { SearchInput } from "../components/SearchInput";
import { oneOf, searchUpdate, text } from "@/lib/search";
import { PROGRAMME_LABELS, Programme, type ProgrammeValue } from "@convex/schemaEnums";
import { fold } from "@/lib/highlightMatch";
import { RoleIcon, resolveMemberRole } from "../components/RoleIcon";
import { groupLink } from "@/lib/groupLink";

type CeMMiesResult = FunctionReturnType<typeof api.persons.listCeMMies>;
type CeMMieRow = CeMMiesResult["persons"][number];

// The page means "the institute", so it opens on people with a counting
// affiliation. The rest of the roster is one choice away rather than mixed in:
// alumni still on a wrap-up agreement, and people whose start date is still
// ahead of them, are real records but not headcount.
export const CEMMIE_FILTERS = ["all", "active", "cemmie", "alumniActive", "alumni", "incoming"] as const;
type StatusFilter = (typeof CEMMIE_FILTERS)[number];

const PROGRAMMES = Object.values(Programme);

/**
 * The page's filters, from the address. Position is one of two kinds: a
 * position (?position=postdoctoralFellow), or enrolment in a programme,
 * optionally one cohort of it (?programme=phd&cohort=2023, cohort=none for
 * those enrolled without one).
 */
export function validateCeMMiesSearch(search: Record<string, unknown>): {
  status?: StatusFilter;
  q?: string;
  position?: string;
  programme?: ProgrammeValue;
  cohort?: string;
} {
  const programme = oneOf(search.programme, PROGRAMMES);
  return {
    status: oneOf(search.status, CEMMIE_FILTERS),
    q: text(search.q),
    position: programme === undefined ? text(search.position) : undefined,
    programme,
    cohort: programme === undefined ? undefined : text(search.cohort),
  };
}

// The position dropdown's value, one string per option: "" for everyone,
// "position:<key>", "programme:<programme>" or "programme:<programme>:<cohort>".
function positionMatches(person: CeMMieRow, value: string): boolean {
  if (value === "") {
    return true;
  }
  const [kind, key, cohort] = value.split(":");
  if (kind === "position") {
    // Any of their positions, as the Position column lists them all.
    return person.affiliations.some((aff) => aff.positionKey === key);
  }
  const enrollment = person.enrollment;
  if (enrollment === null || enrollment.programme !== key) {
    return false;
  }
  return cohort === undefined || String(enrollment.cohort ?? "none") === cohort;
}

// Status is derived from affiliations, not from a flag: CeMMie, Alumni
// (active) for someone still on a collaboration agreement, Alumni, Incoming.
function DirectoryStatusBadge({ row }: { row: CeMMieRow }) {
  const palette =
    row.status === "cemmie"
      ? "green"
      : row.status === "alumniActive"
        ? "teal"
        : row.status === "incoming"
          ? "blue"
          : "gray";
  return (
    <Badge
      colorPalette={palette}
      variant={row.status === "cemmie" ? "solid" : "subtle"}
    >
      {row.statusLabel}
    </Badge>
  );
}

const columnHelper = createColumnHelper<CeMMieRow>();

// Defined once, outside the component. An inline arrow is a new
// function on every render, and a table told its row identity has
// changed cannot skip anything.
const getRowId = (row: { _id: string }) => row._id;

export function CeMMiesPage() {
  const result = useQuery(api.persons.listCeMMies, {});
  // The current CeMMies unless the address says otherwise (?status=alumni).
  const params = useSearch({ from: "/_authed/cemmies" });
  const filter: StatusFilter = params.status ?? "cemmie";
  const search = params.q ?? "";
  const navigate = useNavigate({ from: "/cemmies" });
  const setFilter = (next: StatusFilter) =>
    void navigate({ search: (prev) => ({ ...prev, status: next === "cemmie" ? undefined : next }), ...searchUpdate });
  const setSearch = (next: string) =>
    void navigate({ search: (prev) => ({ ...prev, q: next === "" ? undefined : next }), ...searchUpdate });
  const position = params.programme !== undefined
    ? `programme:${params.programme}${params.cohort === undefined ? "" : `:${params.cohort}`}`
    : params.position === undefined ? "" : `position:${params.position}`;
  const setPosition = (value: string) => {
    const [kind, key, cohort] = value.split(":");
    void navigate({
      search: (prev) => ({
        ...prev,
        position: kind === "position" ? key : undefined,
        programme: kind === "programme" ? key as ProgrammeValue : undefined,
        cohort: kind === "programme" ? cohort : undefined,
      }),
      ...searchUpdate,
    });
  };

  const allPersons = useMemo(() => result?.persons ?? [], [result]);
  // What a person is looked up by, folded once per load: name, short name,
  // email and position. Not their group: groups are named after their heads,
  // so searching a head's name listed everybody in the group.
  const searchable = useMemo(
    () => new Map(allPersons.map((person) => [person._id, fold([
      person.fullName,
      person.shortName ?? "",
      person.email ?? "",
      ...person.affiliations.map((aff) => aff.positionLabel),
    ].join("\n"))])),
    [allPersons],
  );
  // The search narrows everything below it, the status chips' counts
  // included: the page opens on current CeMMies, so somebody who has left is
  // found by a count appearing on Alumni rather than by an empty table.
  const persons = useMemo(() => {
    const needle = fold(search.trim());
    return needle === ""
      ? allPersons
      : allPersons.filter((person) => searchable.get(person._id)?.includes(needle));
  }, [allPersons, searchable, search]);
  // Active means holding a current affiliation, which is also what grants
  // access -- a CeMMie and an alumnus finishing a manuscript on a wrap-up
  // agreement are both here and both able to sign in. Alumni and incoming sit
  // beside it rather than under an "inactive" parent: nobody asks for "everyone
  // who is not here", and the parent only existed to hold two unrelated
  // answers.
  const STATUSES_IN: Record<StatusFilter, readonly string[] | null> = {
    all: null,
    active: ["cemmie", "alumniActive"],
    cemmie: ["cemmie"],
    alumniActive: ["alumniActive"],
    alumni: ["alumni"],
    incoming: ["incoming"],
  };

  const matches = (status: string, key: StatusFilter) => {
    const allowed = STATUSES_IN[key];
    return allowed === null || allowed.includes(status);
  };

  const data = useMemo<CeMMieRow[]>(
    () => persons.filter((person) => matches(person.status, filter) && positionMatches(person, position)),
    [persons, filter, position],
  );

  // Each control's counts follow the others: the chips count within the
  // chosen position, the dropdown within the chosen status.
  const countOf = (key: StatusFilter) =>
    persons.filter((person) => matches(person.status, key) && positionMatches(person, position)).length;
  const positionCount = (value: string) =>
    persons.filter((person) => matches(person.status, filter) && positionMatches(person, value)).length;

  // Every position anybody holds or held, A-Z, and each programme with its
  // cohorts, newest first -- the cohorts still in the building are the ones
  // looked for. Built from everybody, so the list does not change under a
  // search; what changes is the counts.
  const positionChoices = useMemo(() => {
    const positions = new Map<string, string>();
    const cohorts = new Map<ProgrammeValue, Set<string>>();
    for (const person of allPersons) {
      for (const aff of person.affiliations) {
        positions.set(aff.positionKey, aff.positionLabel);
      }
      if (person.enrollment !== null) {
        const seen = cohorts.get(person.enrollment.programme) ?? new Set<string>();
        seen.add(String(person.enrollment.cohort ?? "none"));
        cohorts.set(person.enrollment.programme, seen);
      }
    }
    return {
      positions: [...positions].sort(([, left], [, right]) => left.localeCompare(right)),
      programmes: PROGRAMMES
        .filter((programme) => cohorts.has(programme))
        .map((programme) => ({
          programme,
          cohorts: [...cohorts.get(programme)!].sort((left, right) =>
            left === "none" ? 1 : right === "none" ? -1 : Number(right) - Number(left)),
        })),
    };
  }, [allPersons]);

  // An option nobody matches stays listed, so the list does not jump, but
  // cannot be chosen -- unless it is the one already chosen.
  const positionOption = (value: string, label: string) => {
    const count = positionCount(value);
    return (
      <option key={value} value={value} disabled={count === 0 && value !== position}>
        {label} ({count})
      </option>
    );
  };

  // Everyone is the state before choosing anything, so it gets no chip.
  const filterOptions: FilterNode<StatusFilter>[] = [
    {
      key: "active",
      label: "Active",
      count: countOf("active"),
      description: "Currently at CeMM",
      children: [
        {
          key: "cemmie",
          label: "CeMMies",
          count: countOf("cemmie"),
          description: "At CeMM and counted in the headcount",
        },
        {
          key: "alumniActive",
          label: "Alumni (active)",
          count: countOf("alumniActive"),
          description:
            "Has left, but is still finishing work here, such as a manuscript or thesis. Not in the headcount.",
        },
      ],
    },
    {
      key: "alumni",
      label: "Alumni",
      count: countOf("alumni"),
      description: "Has left CeMM",
    },
    {
      key: "incoming",
      label: "Incoming",
      count: countOf("incoming"),
      description: "Starts at CeMM in the future",
    },
  ];

  const needsPrimary = result?.stats.needsPrimary ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor("fullName", {
        header: "Name",
        cell: ({ row }) => {
          const person = row.original;
          return (
            <HStack gap={3}>
              <Avatar.Root size="md">
                <Avatar.Fallback name={person.fullName} />
                {person.profileImageUrl ? (
                  <Avatar.Image src={person.profileImageUrl} alt={`${person.fullName} profile`} />
                ) : null}
              </Avatar.Root>
              <Box>
                {person.shortName ? (
                  <Link to="/cemmies/$shortName" params={{ shortName: person.shortName }}>
                    <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{person.fullName}</Text>
                  </Link>
                ) : (
                  <Text fontWeight="semibold">{person.fullName}</Text>
                )}
                <HStack gap={2}>
                  <Text fontSize="xs" color="fg.muted">{person.shortName ?? "No user short name"}</Text>
                  {/* Elected representative bodies, from the Entra groups that
                      double as their mailing lists. Administration members in
                      those lists are excluded, since membership alone never
                      establishes the role. */}
                  {person.representativeRoles.map((role) => (
                    <Badge key={role} colorPalette="purple" variant="subtle">
                      {role}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            </HStack>
          );
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        // A link, underlined on hover like the name beside it: the cell is
        // nothing but the address.
        cell: ({ row }) => row.original.email ? (
          <a href={`mailto:${row.original.email}`}>
            <HStack gap={2} fontSize="sm" color="fg.muted" _hover={{ color: "blue.fg" }}>
              <Icon as={LuMail} boxSize={4} />
              <Text _hover={{ textDecoration: "underline" }}>{row.original.email}</Text>
            </HStack>
          </a>
        ) : (
          <Text fontSize="sm" color="fg.muted">—</Text>
        ),
      }),
      columnHelper.display({
        id: "job",
        header: "Position",
        cell: ({ row }) => {
          const affs = row.original.affiliations;
          const enrollment = row.original.enrollment;
          const isHead = affs.some((aff) => aff.headRole === "head");
          // The controlled position, not the raw job title.
          const titles = [...new Set(affs.map((aff) => aff.positionLabel).filter(Boolean))];

          if (enrollment) {
            const title = titles[0] ?? "Predoctoral Fellow";
            return (
              <Stack gap={1} fontSize="sm" color="fg.muted">
                <Text>{title}</Text>
                {enrollment.cohort === null ? null : (
                  <Text fontSize="xs">Cohort {enrollment.cohort}</Text>
                )}
              </Stack>
            );
          }

          const label = titles.length > 0 ? titles.join(" · ") : (isHead ? "Head" : null);
          if (!label) {
            return <Text fontSize="sm" color="fg.muted">—</Text>;
          }
          return <Text fontSize="sm" color="fg.muted">{label}</Text>;
        },
      }),
      columnHelper.display({
        id: "groups",
        header: "Groups",
        cell: ({ row }) => {
          const affs = row.original.affiliations;
          const isPhd = row.original.enrollment != null;
          if (affs.length === 0) {
            return <Text fontSize="sm" color="fg.muted">—</Text>;
          }
          return (
            <HStack
              gap={8}
              align="start"
              wrap="nowrap"
              minW={affs.length > 1 ? "380px" : "200px"}
              fontSize="sm"
              color="fg.muted"
            >
              {affs.map((aff, index) => {
                // Shared with the unit pages, so a person is the same symbol
                // wherever they appear.
                const role = resolveMemberRole({ headRole: aff.headRole, isStudent: isPhd });
                return (
                  <Stack key={aff.orgUnit?.slug ?? index} gap={1} flexShrink={0}>
                    <HStack gap={2}>
                      <RoleIcon role={role} />
                      {aff.orgUnit ? (
                        <Link to="/groups/$shortName" {...groupLink(aff.orgUnit)}>
                          <Text fontWeight="medium" color="fg" whiteSpace="nowrap" _hover={{ textDecoration: "underline" }}>{aff.orgUnit.name}</Text>
                        </Link>
                      ) : (
                        <Text whiteSpace="nowrap">—</Text>
                      )}
                    </HStack>
                    {aff.orgUnit ? (
                      <Text fontSize="xs">
                        {aff.orgUnit.shortName}
                        {aff.orgUnit.typeLabel ? ` · ${aff.orgUnit.typeLabel}` : ""}
                        {/* A non-counting basis: on site, but outside the headcount. */}
                        {aff.countsAsCeMMie ? "" : ` · ${aff.basisLabel}`}
                      </Text>
                    ) : null}
                  </Stack>
                );
              })}
            </HStack>
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: ({ row }) => <DirectoryStatusBadge row={row.original} />,
      }),
    ],
    [],
  );

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="CeMMies"
        description="Everyone at CeMM, past and present, with their group and position."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "CeMMies" }]}
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <Stack gap={4}>
          <Stack direction={{ base: "column", lg: "row" }} gap={3}>
            <SearchInput
              flex="1"
              placeholder="Search by name, email or position"
              value={search}
              onChange={setSearch}
            />
            <NativeSelect.Root size="sm" w={{ base: "full", lg: "20rem" }}>
              <NativeSelect.Field
                aria-label="Position"
                value={position}
                onChange={(event) => setPosition(event.target.value)}
              >
                <option value="">All positions</option>
                {positionChoices.programmes.map(({ programme, cohorts }) => (
                  <optgroup key={programme} label={`${PROGRAMME_LABELS[programme]} programme`}>
                    {positionOption(`programme:${programme}`, "All cohorts")}
                    {cohorts.map((cohort) => positionOption(
                      `programme:${programme}:${cohort}`,
                      cohort === "none" ? "No cohort" : `${cohort} cohort`,
                    ))}
                  </optgroup>
                ))}
                <optgroup label="Positions">
                  {positionChoices.positions.map(([key, label]) => positionOption(`position:${key}`, label))}
                </optgroup>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Stack>
          <FilterBar
            options={filterOptions}
            value={filter}
            clearKey="all"
            onChange={setFilter}
            loading={result === undefined}
          />
        </Stack>

        {/* The count belongs with the table it describes, not with the
            filters above. */}
        <Stack gap={2} align="stretch">
          {result === undefined ? (
            <Box minH={8} display="flex" alignItems="center"><Skeleton height="4" width="10rem" /></Box>
          ) : (
            <FilterCount
              shown={data.length}
              total={allPersons.length}
              noun={["person", "people"]}
              filtered={filter !== "all" || search.trim() !== "" || position !== ""}
              onClear={() => void navigate({ search: { status: "all" }, ...searchUpdate })}
            >
              {needsPrimary > 0 ? ` · ${needsPrimary} still need a primary group` : null}
            </FilterCount>
          )}
          {result === undefined ? (
            <TableSkeleton label="Loading people" rows={10} columns={5} />
          ) : data.length === 0 ? (
            <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
              <Card.Body gap={2} p={8} textAlign="center">
                <Heading size="lg">Nobody in this view</Heading>
                <Text fontSize="sm" color="fg.muted">
                  {result.persons.length === 0
                    ? "Nobody is in the directory yet."
                    : (search.trim() !== "" || position !== "") && countOf("all") > 0
                      // Found, but under another status: the chips say which.
                      ? "Nobody here matches. The counts above show where somebody does."
                      : "No one matches. Try another search or filter."}
                </Text>
              </Card.Body>
            </Card.Root>
          ) : (
            <Box borderWidth="1px" borderColor="border">
              <DataTable
                data={data}
                columns={columns}
                getRowId={getRowId}
                enableSorting
                stickyHeader
                progressive
                initialBatchSize={20}
                progressiveBatchSize={500}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
